<template>
  <div class="page-wrap" v-if="!pageLoading">
    <page-title
      :moduleName="moduleName"
      :title="getText('detail')"
      class="detail-page-title"
    ></page-title>
    <div class="page-inner-wrap">
      <close-btn :moduleName="moduleName"></close-btn>
      <article-banner
        :banner="getIco('contentbanner')"
        class="banner"
      ></article-banner>
      <div class="detail-wrap">
        <p class="title">{{ articleContent.title }}</p>
        <div
          class="content"
          id="articleContent"
          v-html="articleContent.content"
          @click="handleATagHref"
        ></div>
      </div>
      <article-tag
        :list="tagList"
        class="tag"
        @chooseTag="chooseTag"
      ></article-tag>
      <like-btn
        v-if="showLikeBtn"
        :isLike="isLike"
        :unLikeIco="getIco('likebtn')"
        :likedIco="getIco('likebtn_s')"
        @like="handleLike"
        @cancelLike="handleCancelLike"
      ></like-btn>
    </div>
    <nav-bar></nav-bar>
  </div>
</template>
<script setup lang="ts">
import { reactive, ref, onActivated, nextTick } from 'vue';
import PageTitle from '@components/common/PageTitle.vue';
import CloseBtn from '@components/common/CloseBtn.vue';
import ArticleBanner from '@components/article/ArticleBanner.vue';
import NavBar from '@components/common/NavBar.vue';
import ArticleTag from '@components/article/ArticleTag.vue';
import LikeBtn from '@components/like/LikeBtn.vue';
import { getIco, getText } from '@config/siteConfig';
import useLike from '@hooks/useLike';
import { sdkOpenLink } from '@utiles/sdkCtrl';
import { useRoute, useRouter } from 'vue-router';
import { useBaseStore } from '@store/base';
import useModule from '@hooks/useModule';
import useLoading from '@hooks/useLoading';
import { getArticleDetail } from '@api/article';
import { useArticleStore } from '@store/article';
import usePointViewPage from '@hooks/usePointViewPage';
const { setLoadingState, pageLoading } = useLoading();
const { moduleName, goFirstModulePage, waitUntilGetLangSucc } = useModule();
const { sendViewPagePoint } = usePointViewPage({
  pageKey: pageType.articleDetailPage,
  moduleName,
  isAutoSendViewPage: false,
});

const route = useRoute();
const router = useRouter();

const article = useArticleStore();
const base = useBaseStore();

let articleId = ref('');
let articleGroupId = ref('');
const tagList = ref([]);
const articleContent = reactive({
  title: '',
  content: '',
});

const { isLike, updateLikeData, like, cancelLike, showLikeBtn } =
  useLike('article');

const handleLike = () => {
  like();
  clickButtonPoint({
    btnName: 'like',
    moduleName: moduleName,
    otherParams: {
      likeType: 'like',
      articleId: articleId.value,
      articleGroupId: articleGroupId.value,
    },
  });
};

const handleCancelLike = () => {
  cancelLike();
  clickButtonPoint({
    btnName: 'like',
    moduleName: moduleName,
    otherParams: {
      likeType: 'cancelLike',
      articleId: articleId.value,
      articleGroupId: articleGroupId.value,
    },
  });
};

const getDetail = async (id) => {
  setLoadingState(true);
  await waitUntilGetLangSucc();
  getArticleDetail({
    group_id: id,
    name: base.name,
    language: base.lang,
    preview: base.preview,
    sign: base.sign,
  }).then((res: any) => {
    const detail = res?.data?.detail;
    if (!detail || !detail?.CONTENT) {
      setLoadingState(false);
      goFirstModulePage();
      return;
    }
    if (detail) {
      sendViewPagePoint(detail?.ARTICLE_GROUP_ID);
      articleContent.title = detail?.TITLE || '';
      articleContent.content =
        detail?.CONTENT.replace(/href/g, 'data-href') || '';
      articleId.value = detail?.ID;
      showLikeBtn.value && updateLikeData(articleId.value);
      setLoadingState(false);
    }
    const taglist = res?.data?.tagList;
    if (taglist?.length > 0) {
      tagList.value = taglist.map((item) => {
        return {
          CONTENT: JSON.parse(item.CONTENT || '{}')[base.lang] || '',
          ID: item.ID,
          NAME: item.NAME,
        };
      });
    } else {
      tagList.value = [];
    }
  });
};

const resetArticleContent = () => {
  articleContent.title = '';
  articleContent.content = '';
};

onActivated(() => {
  let groupId = route.query.groupId as string;
  if (!groupId) {
    goFirstModulePage();
    return;
  }
  if (articleGroupId.value != groupId) {
    resetArticleContent();
    articleGroupId.value = groupId;
    getDetail(articleGroupId.value);
  }
});

const handleATagHref = (event) => {
  const target: HTMLElement = event.target || event.srcElement;
  const father: HTMLElement = target.parentElement;

  if (
    target.nodeName.toLocaleLowerCase() !== 'a' &&
    father.nodeName.toLocaleLowerCase() !== 'a'
  ) {
    return;
  }

  const link =
    target.getAttribute('data-href') || father.getAttribute('data-href');
  if (!link) {
    return;
  }
  if (link.includes('/articledetail')) {
    const fragment = link.split('#')[1];
    const queryParams = fragment.split('?')[1];
    const searchParams = new URLSearchParams(queryParams);
    const groupId = searchParams.get('groupId');
    const source = searchParams.get('source');
    router
      .push({
        path: '/articledetail',
        query: {
          groupId: groupId,
          source: source,
        },
      })
      .then(() => {
        nextTick(() => {
          window.location.reload();
        });
      });
    return;
  }
  const openWay = target.getAttribute('target');
  if (openWay && openWay === '_blank') {
    sdkOpenLink({
      link,
      openWay: 2,
      loginDir: '1',
      appendParams: '1',
      source: pageSourceType.articleDetailPage,
    });
    return;
  }

  sdkOpenLink({
    link,
    openWay: 0,
    loginDir: '1',
    appendParams: '1',
    source: pageSourceType.articleDetailPage,
  });
};

const chooseTag = (id) => {
  const tagInfo = tagList.value.find((item) => item.ID == id);
  article.setSeatchTag({
    text: tagInfo.CONTENT,
    id: tagInfo.ID,
  });
  clickButtonPoint({
    btnName: 'tags',
    moduleName: moduleName,
    otherParams: {
      tagId: tagInfo.ID,
    },
  });
  router.push({
    path: '/search',
    query: {
      source: pageSourceType.articleDetailTag,
    },
  });
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/dimen.scss';
.page-wrap {
  overflow-x: hidden;
  .page-inner-wrap {
    padding: 0 30px;
    margin-top: 112px;
  }
  .detail-wrap {
    width: 100%;
    margin: 0 auto;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    .title {
      text-align: center;
      font-weight: bold;
      width: 100%;
      margin: 57px 0;
      line-height: 35px;
      font-size: $fzDetailTitle;
      color: $itemcolor;
    }
    .content {
      line-height: 200%;
      font-size: $fzNormal;
      color: $itemcolor;
      word-break: break-word;
      &:deep(img) {
        max-width: 100% !important;
        height: auto !important;
      }
      &:deep(h1) {
        font-size: 38px;
        color: $maincolor;
      }
      &:deep(h2) {
        font-size: 32px;
        color: $maincolor;
      }
      &:deep(h3) {
        font-size: 30px;
        color: $maincolor;
      }
      &:deep(strong) {
        font-weight: bolder;
      }
    }
  }
  .tag {
    margin-top: 62px;
    &:deep(.tag) {
      color: $maincolor;
    }
  }
}
@media (orientation: landscape) {
  .page-inner-wrap {
    padding: 0 40px;
    margin-top: 112px;
  }
  .banner {
    display: none;
  }
}

@media only screen and (min-height: 640px) and (orientation: landscape) {
  .page-wrap {
    .detail-wrap {
      .title {
        margin: calc(57px / $pcRate) 0;
        line-height: calc(35px / $pcRate);
        font-size: $fzDetailTitlePc;
      }
      .content {
        font-size: $fzNormalPc;
        &:deep(h1) {
          font-size: calc(38px / $pcRate);
        }
        &:deep(h2) {
          font-size: calc(32px / $pcRate);
        }
        &:deep(h3) {
          font-size: calc(30px / $pcRate);
        }
      }
    }
    .tag {
      margin-top: calc(62px / $pcRate);
    }
  }
}
</style>
